import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Gift, Loader2, Image as ImageIcon, Upload, X, 
  AlertCircle, CheckCircle2, Info, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import CloudinaryUploader from '../../components/CloudinaryUploader';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg";

const BENEFIT_CATEGORIES = [
  { value: 'gastronomia', label: 'Gastronomía', description: 'Restaurantes, bares, cafeterías' },
  { value: 'entretenimiento', label: 'Entretenimiento', description: 'Cine, teatro, eventos' },
  { value: 'servicios', label: 'Servicios', description: 'Peluquerías, spa, gimnasios' },
  { value: 'retail', label: 'Retail', description: 'Tiendas, indumentaria, accesorios' },
  { value: 'salud', label: 'Salud y Bienestar', description: 'Farmacias, ópticas, clínicas' },
  { value: 'educacion', label: 'Educación', description: 'Cursos, talleres, capacitaciones' },
  { value: 'otro', label: 'Otro', description: 'Especificar categoría' },
];

const IMAGE_SPECS = {
  formats: ['JPG', 'PNG', 'WebP'],
  maxSize: '5 MB',
  minWidth: 800,
  minHeight: 600,
  aspectRatio: '4:3 o 16:9',
  recommended: '1200 x 900 px'
};

const CreateBenefitPage = () => {
  const navigate = useNavigate();
  const { benefitId } = useParams();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const isEditing = !!benefitId;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    custom_category: '',
    terms: '',
    image_url: '',
    max_uses_per_user: 1,  // Keep for backwards compatibility
    limited_coupons: 0  // 0 = unlimited, > 0 = limited stock
  });

  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isEditing && partner) {
      loadBenefit();
    }
  }, [isEditing, partner]);

  const loadBenefit = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const response = await api.get('/partners/me/benefits', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const benefit = response.data.find(b => b.id === benefitId);
      if (benefit) {
        const isCustomCategory = !BENEFIT_CATEGORIES.some(c => c.value === benefit.category);
        setFormData({
          title: benefit.title || '',
          description: benefit.description || '',
          category: isCustomCategory ? 'otro' : (benefit.category || ''),
          custom_category: isCustomCategory ? benefit.category : '',
          terms: benefit.terms || '',
          image_url: benefit.image_url || '',
          max_uses_per_user: benefit.max_uses_per_user || 1,
          limited_coupons: benefit.limited_coupons || 0
        });
        if (benefit.image_url) {
          setImagePreview(benefit.image_url);
        }
      }
    } catch (error) {
      console.error('Error loading benefit:', error);
      toast.error('Error al cargar el beneficio');
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('partner_token');
    const partnerData = localStorage.getItem('partner_data');
    
    if (!token || !partnerData) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await api.get('/partners/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartner(response.data);
    } catch (error) {
      localStorage.removeItem('partner_token');
      localStorage.removeItem('partner_data');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'El título es obligatorio';
    } else if (formData.title.length < 5) {
      newErrors.title = 'El título debe tener al menos 5 caracteres';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    } else if (formData.description.length < 20) {
      newErrors.description = 'La descripción debe tener al menos 20 caracteres';
    }
    
    if (!formData.category) {
      newErrors.category = 'Selecciona una categoría';
    }
    
    if (formData.category === 'otro' && !formData.custom_category.trim()) {
      newErrors.custom_category = 'Especifica la categoría';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [uploadingImage, setUploadingImage] = useState(false);

  const processFile = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato no válido. Usa JPG, PNG o WebP');
      return;
    }

    // Validate file size (5MB max for Cloudinary)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar los 5 MB');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    
    // Upload to Cloudinary automatically
    setUploadingImage(true);
    try {
      // Get signature from backend
      const sigResponse = await api.get('/cloudinary/signature', {
        params: {
          folder: 'bravo/benefits',
          resource_type: 'image'
        }
      });

      const { signature, timestamp, cloud_name, api_key } = sigResponse.data;

      // Prepare form data for Cloudinary
      const cloudinaryFormData = new FormData();
      cloudinaryFormData.append('file', file);
      cloudinaryFormData.append('signature', signature);
      cloudinaryFormData.append('timestamp', timestamp);
      cloudinaryFormData.append('api_key', api_key);
      cloudinaryFormData.append('folder', 'bravo/benefits');

      // Upload to Cloudinary directly
      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        {
          method: 'POST',
          body: cloudinaryFormData
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Error al subir imagen');
      }

      const result = await uploadResponse.json();
      
      // Set the permanent URL
      setFormData(prev => ({ ...prev, image_url: result.secure_url }));
      setImagePreview(result.secure_url);
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading to Cloudinary:', error);
      toast.error('Error al subir la imagen. Intenta de nuevo.');
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor corrige los errores del formulario');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('partner_token');
      
      const benefitData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category === 'otro' ? formData.custom_category.trim() : formData.category,
        terms: formData.terms.trim() || null,
        image_url: formData.image_url || null,
        max_uses_per_user: parseInt(formData.max_uses_per_user) || 1,
        limited_coupons: parseInt(formData.limited_coupons) || 0
      };

      if (isEditing) {
        await api.put(`/partners/me/benefits/${benefitId}`, benefitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Beneficio actualizado y enviado para aprobación');
      } else {
        await api.post('/partners/me/benefits', benefitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Beneficio creado y enviado para aprobación');
      }
      
      navigate('/partner/dashboard');
    } catch (error) {
      console.error('Error saving benefit:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el beneficio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex-shrink-0">
                <img src={LOGO_URL} alt="Bravo" className="h-14 w-auto" />
              </Link>
              <div className="h-10 w-px bg-[#27272A]" />
              <div>
                <h1 className="font-bold text-white text-lg leading-tight">{partner?.business_name}</h1>
                <p className="text-[#71717A] text-xs uppercase tracking-wider">Portal Partners</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/partner/dashboard" 
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Volver al Dashboard
          </Link>
        </motion.div>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.3)]">
              <Gift size={28} className="text-black" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">
                {isEditing ? 'Editar Beneficio' : 'Crear Nuevo Beneficio'}
              </h1>
              <p className="text-[#A1A1AA]">
                {isEditing 
                  ? 'Modifica los datos de tu beneficio. Requiere nueva aprobación.'
                  : 'Completá el formulario para agregar un beneficio a FULL BRAVX'
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 sm:p-8"
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <Sparkles size={22} className="text-[#D4AF37]" />
              Información del Beneficio
            </h2>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <Label className="text-sm font-semibold text-white mb-2 block">
                  Título del Beneficio *
                </Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ej: 20% de descuento en consumiciones"
                  className={`h-14 bg-[#18181B] border-2 text-white text-lg placeholder:text-[#52525B] focus:border-[#D4AF37] transition-all ${
                    errors.title ? 'border-red-500' : 'border-[#27272A]'
                  }`}
                  maxLength={100}
                />
                {errors.title && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle size={14} /> {errors.title}
                  </p>
                )}
                <p className="text-[#52525B] text-xs mt-2">{formData.title.length}/100 caracteres</p>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-semibold text-white mb-2 block">
                  Descripción *
                </Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe detalladamente el beneficio que ofreces..."
                  rows={4}
                  className={`w-full px-4 py-4 bg-[#18181B] border-2 rounded-xl text-white text-base placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 focus:outline-none resize-none transition-all ${
                    errors.description ? 'border-red-500' : 'border-[#27272A]'
                  }`}
                  maxLength={500}
                />
                {errors.description && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle size={14} /> {errors.description}
                  </p>
                )}
                <p className="text-[#52525B] text-xs mt-2">{formData.description.length}/500 caracteres</p>
              </div>

              {/* Category */}
              <div>
                <Label className="text-sm font-semibold text-white mb-2 block">
                  Categoría del Cupón *
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {BENEFIT_CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.category === cat.value
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-[#27272A] bg-[#18181B] hover:border-[#3F3F46]'
                      }`}
                    >
                      <p className={`font-semibold ${formData.category === cat.value ? 'text-[#D4AF37]' : 'text-white'}`}>
                        {cat.label}
                      </p>
                      <p className="text-[#71717A] text-xs mt-1">{cat.description}</p>
                    </button>
                  ))}
                </div>
                {errors.category && (
                  <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                    <AlertCircle size={14} /> {errors.category}
                  </p>
                )}

                {/* Custom Category Input */}
                {formData.category === 'otro' && (
                  <div className="mt-4">
                    <Input
                      value={formData.custom_category}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_category: e.target.value }))}
                      placeholder="Especifica la categoría..."
                      className={`h-12 bg-[#18181B] border-2 text-white placeholder:text-[#52525B] focus:border-[#D4AF37] ${
                        errors.custom_category ? 'border-red-500' : 'border-[#27272A]'
                      }`}
                    />
                    {errors.custom_category && (
                      <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                        <AlertCircle size={14} /> {errors.custom_category}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Limited Coupons */}
              <div className="mt-6 pt-6 border-t border-[#27272A]">
                <Label className="text-sm font-semibold text-white mb-3 block">
                  Cupones Limitados
                </Label>
                <p className="text-[#71717A] text-sm mb-4">
                  ¿Cuántos cupones en total querés ofrecer? Cuando se agoten, el beneficio se desactivará automáticamente.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, limited_coupons: 0 }))}
                    className={`px-5 py-3 rounded-xl border-2 font-semibold transition-all ${
                      formData.limited_coupons === 0
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'border-[#27272A] bg-[#18181B] text-white hover:border-[#3F3F46]'
                    }`}
                  >
                    Ilimitados
                  </button>
                  {[5, 10, 20, 50, 100].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, limited_coupons: num }))}
                      className={`px-5 py-3 rounded-xl border-2 font-semibold transition-all ${
                        formData.limited_coupons === num
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                          : 'border-[#27272A] bg-[#18181B] text-white hover:border-[#3F3F46]'
                      }`}
                    >
                      {num} cupones
                    </button>
                  ))}
                </div>
                {![0, 5, 10, 20, 50, 100].includes(formData.limited_coupons) && formData.limited_coupons > 0 && (
                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-[#71717A] text-sm">Personalizado:</span>
                    <Input
                      type="number"
                      min="1"
                      value={formData.limited_coupons}
                      onChange={(e) => setFormData(prev => ({ ...prev, limited_coupons: parseInt(e.target.value) || 0 }))}
                      className="w-24 h-10 bg-[#18181B] border-[#27272A] text-white text-center"
                    />
                    <span className="text-[#71717A] text-sm">cupones</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, limited_coupons: 25 }))}
                  className="mt-3 text-[#D4AF37] text-sm hover:underline"
                >
                  Especificar otro número
                </button>
                {formData.limited_coupons > 0 && (
                  <div className="mt-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3">
                    <p className="text-[#D4AF37] text-sm">
                      Se generarán <strong>{formData.limited_coupons}</strong> cupones. Cuando se agoten, el beneficio se desactivará automáticamente.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.section>

          {/* Terms Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 sm:p-8"
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <Info size={22} className="text-[#D4AF37]" />
              Términos y Condiciones
            </h2>

            <div>
              <Label className="text-sm font-semibold text-white mb-2 block">
                Condiciones del Beneficio
              </Label>
              <textarea
                value={formData.terms}
                onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                placeholder="Ej: Válido de lunes a jueves. No acumulable con otras promociones. Presentar código al momento de pagar..."
                rows={5}
                className="w-full px-4 py-4 bg-[#18181B] border-2 border-[#27272A] rounded-xl text-white text-base placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 focus:outline-none resize-none transition-all"
                maxLength={1000}
              />
              <p className="text-[#52525B] text-xs mt-2">{formData.terms.length}/1000 caracteres (opcional)</p>
            </div>
          </motion.section>

          {/* Image Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 sm:p-8"
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
              <ImageIcon size={22} className="text-[#D4AF37]" />
              Imagen del Beneficio
            </h2>

            {/* Image Specs */}
            <div className="bg-[#18181B] border border-[#27272A] rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-white mb-3 text-sm uppercase tracking-wider">
                Especificaciones de la imagen
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-[#71717A]">Formatos</p>
                  <p className="text-white font-medium">{IMAGE_SPECS.formats.join(', ')}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Tamaño máximo</p>
                  <p className="text-white font-medium">{IMAGE_SPECS.maxSize}</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Dimensión mínima</p>
                  <p className="text-white font-medium">{IMAGE_SPECS.minWidth} x {IMAGE_SPECS.minHeight} px</p>
                </div>
                <div>
                  <p className="text-[#71717A]">Aspecto</p>
                  <p className="text-white font-medium">{IMAGE_SPECS.aspectRatio}</p>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-[#71717A]">Recomendado</p>
                  <p className="text-[#D4AF37] font-medium">{IMAGE_SPECS.recommended}</p>
                </div>
              </div>
            </div>

            {/* Image Upload Area */}
            <div className="space-y-4">
              {imagePreview || formData.image_url ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-[#27272A]">
                  <img 
                    src={imagePreview || formData.image_url} 
                    alt="Preview" 
                    className="w-full h-64 object-cover"
                  />
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center">
                      <Loader2 size={40} className="text-[#D4AF37] animate-spin mb-3" />
                      <p className="text-white font-medium">Subiendo imagen...</p>
                    </div>
                  )}
                  {!uploadingImage && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-3 right-3 w-10 h-10 bg-red-500/90 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                    >
                      <X size={20} className="text-white" />
                    </button>
                  )}
                  {!uploadingImage && formData.image_url && (
                    <div className="absolute bottom-3 left-3 bg-green-500/90 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <CheckCircle2 size={14} />
                      Imagen guardada
                    </div>
                  )}
                </div>
              ) : (
                <label 
                  className="block cursor-pointer"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className={`border-2 border-dashed rounded-xl p-10 text-center transition-all ${
                    isDragging 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 scale-[1.02]' 
                      : 'border-[#27272A] hover:border-[#D4AF37]/50'
                  }`}>
                    <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 transition-colors ${
                      isDragging ? 'bg-[#D4AF37]/20' : 'bg-[#18181B]'
                    }`}>
                      <Upload size={28} className={isDragging ? 'text-[#D4AF37]' : 'text-[#71717A]'} />
                    </div>
                    <p className={`font-semibold mb-2 ${isDragging ? 'text-[#D4AF37]' : 'text-white'}`}>
                      {isDragging ? '¡Suelta la imagen aquí!' : 'Arrastra una imagen o haz clic para seleccionar'}
                    </p>
                    <p className="text-[#71717A] text-sm">JPG, PNG o WebP - Máx. 5 MB</p>
                    <p className="text-[#52525B] text-xs mt-2">La imagen se subirá automáticamente</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}

              {/* Alternative: URL Input */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#27272A]" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-[#121212] text-[#52525B] text-sm">o ingresar URL</span>
                </div>
              </div>

              <Input
                value={formData.image_url}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="h-12 bg-[#18181B] border-2 border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#D4AF37]"
              />
            </div>
          </motion.section>

          {/* Info Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5"
          >
            <div className="flex items-start gap-4">
              <AlertCircle size={24} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-yellow-500 mb-1">Pendiente de Aprobación</h3>
                <p className="text-[#A1A1AA] text-sm">
                  Tu beneficio será revisado por nuestro equipo antes de aparecer en FULL BRAVX. 
                  Este proceso puede tomar hasta 48 horas hábiles. Te notificaremos cuando sea aprobado.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Submit Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 pt-4"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/partner/dashboard')}
              className="flex-1 h-14 border-2 border-[#27272A] text-white hover:border-[#52525B] text-lg"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving || uploadingImage}
              className="flex-1 h-14 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] text-black text-lg font-bold transition-all disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="animate-spin mr-2" size={22} />
              ) : uploadingImage ? (
                <Loader2 className="animate-spin mr-2" size={22} />
              ) : (
                <CheckCircle2 className="mr-2" size={22} />
              )}
              {saving 
                ? (isEditing ? 'Guardando...' : 'Creando...') 
                : uploadingImage
                  ? 'Subiendo imagen...'
                  : (isEditing ? 'Guardar Cambios' : 'Crear Beneficio')
              }
            </Button>
          </motion.div>
        </form>
      </main>
    </div>
  );
};

export default CreateBenefitPage;
